import type { FC } from 'react';
import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Timeline,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import type { SxProps } from '@mui/material';
import { Box, Typography } from '@mui/material';

import { BulkEditContext } from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditDialog';

import { styles } from './index.styles';

export interface BulkEditSummaryProps {
  changedAttributes: any[];
  sx?: SxProps;
}

/**
 * Renders a summary of the chosen value for the changed attributes.
 */
const BulkEditSummary: FC<BulkEditSummaryProps> = ({
  changedAttributes,
  sx = [],
  ...props
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const { formFields } = useContext(BulkEditContext);

  return (
    <Timeline
      className="BulkEditSummary-root"
      sx={{
        ...styles.bulkEditSummary,
        ...(Array.isArray(sx) ? Object.assign({}, ...sx) : sx),
      }}
      {...props}
    >
      {changedAttributes.map(([name, value]) => {
        const field = formFields.find((field) => field.name === name);

        if (!field) return null;

        const choice = field.choices.find((choice) => choice.value === value);

        return (
          <TimelineItem key={name}>
            <TimelineSeparator sx={styles.dotSeparator}>
              <TimelineDot />
            </TimelineSeparator>
            <TimelineContent>
              <Box sx={styles.fieldLabel}>
                <Typography
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {t(field.label)}
                </Typography>
              </Box>
              <Box>
                <Typography color="gray">
                  <Trans
                    i18nKey="admin:BulkEdit.ConfirmationChangeMessage"
                    values={{
                      newValue: t(choice?.label || '-'),
                    }}
                  />
                </Typography>
              </Box>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};
export default BulkEditSummary;
